import { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  Button,
  Alert,
} from '@kollektive/design-system';
import type { TeamMember } from '@/hooks/useTeam';
import styles from './EditMemberModal.module.css';

interface EditMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: TeamMember | null;
  currentUserRole: string;
  onSave: (memberId: string, role: string) => Promise<void>;
  isLoading: boolean;
}

// Available roles depend on current user's role
function getAvailableRoles(currentUserRole: string, _memberRole: string) {
  // Owner can assign any role except owner
  if (currentUserRole === 'owner') {
    return [
      { value: 'admin', label: 'Admin' },
      { value: 'member', label: 'Member' },
      { value: 'viewer', label: 'Viewer' },
    ];
  }

  // Admin can only change between member and viewer
  if (currentUserRole === 'admin') {
    return [
      { value: 'member', label: 'Member' },
      { value: 'viewer', label: 'Viewer' },
    ];
  }

  return [];
}

const ROLE_DESCRIPTIONS: Record<string, string> = {
  admin: 'Admins can manage team members, invitations, and most workspace settings.',
  member: 'Members can create and manage connections and tokens.',
  viewer: 'Viewers have read-only access to workspace data.',
};

export function EditMemberModal({
  isOpen,
  onClose,
  member,
  currentUserRole,
  onSave,
  isLoading,
}: EditMemberModalProps) {
  const [role, setRole] = useState<string>(member?.role || 'member');
  const [error, setError] = useState<string | null>(null);

  // Reset state when member changes
  useEffect(() => {
    if (member) {
      setRole(member.role);
      setError(null);
    }
  }, [member]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!member) return;

    // No change
    if (role === member.role) {
      onClose();
      return;
    }

    try {
      await onSave(member.id, role);
      onClose();
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('FORBIDDEN')) {
          setError('You do not have permission to change this role');
        } else if (err.message.includes('INVALID_ROLE')) {
          setError('Invalid role selected');
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to update member');
      }
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError(null);
      onClose();
    }
  };

  const availableRoles = getAvailableRoles(currentUserRole, member?.role || '');
  const hasChanges = member && role !== member.role;

  return (
    <Modal open={isOpen} onClose={handleClose} size="sm">
      <form onSubmit={handleSubmit}>
        <ModalHeader title="Edit Team Member" onClose={handleClose} />
        <ModalBody>
          {member && (
            <div className={styles.memberInfo}>
              <div className={styles.memberName}>{member.name}</div>
              <div className={styles.memberEmail}>{member.email}</div>
            </div>
          )}

          <div className={styles.formFields}>
            <Select
              label="Role"
              options={availableRoles}
              value={role}
              onChange={(value) => setRole(value)}
              disabled={isLoading}
            />

            <p className={styles.roleDescription}>
              {ROLE_DESCRIPTIONS[role] || ''}
            </p>

            {error && (
              <Alert variant="error" styleVariant="glass">
                {error}
              </Alert>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isLoading || !hasChanges}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
